import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardData, ConsumableType } from '../types';
import { getApiUrl, type ActiveMind } from '../services/api';

const ACTIVE_INTERVAL = 10_000;   // 10s when tab is active
const BACKGROUND_INTERVAL = 30_000; // 30s when tab is hidden

export function useDashboard(token: string | null, mind: ActiveMind = 'hannah') {
  const [data, setData] = useState<DashboardData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(!document.hidden);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const handler = () => setIsActive(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const response = await fetch(`${getApiUrl(mind)}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Dashboard API error: ${response.status}`);
      }

      const result: DashboardData = await response.json();
      setData(result);
      setLastUpdated(Date.now());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard');
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [token, mind]);

  useEffect(() => {
    if (!token) return;
    fetchDashboard();
    const interval = setInterval(fetchDashboard, isActive ? ACTIVE_INTERVAL : BACKGROUND_INTERVAL);
    return () => clearInterval(interval);
  }, [token, fetchDashboard, isActive]);

  const administerConsumable = useCallback(async (type: ConsumableType) => {
    if (!token) return;

    try {
      const response = await fetch(`${getApiUrl(mind)}/consumable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ type }),
      });

      if (!response.ok) {
        const err = await response.json() as { error: string };
        throw new Error(err.error || `Failed: ${response.status}`);
      }

      // Re-fetch dashboard after a short delay to see the nudge effect
      setTimeout(fetchDashboard, 1500);

      return await response.json();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to administer consumable');
      throw err;
    }
  }, [token, mind, fetchDashboard]);

  return {
    data,
    lastUpdated,
    isLoading,
    error,
    refresh: fetchDashboard,
    administerConsumable,
  };
}
